import { makeAutoObservable } from "mobx";
import { startBatch } from "mobx/dist/internal";
import moment from "moment";
import { ChatSubscription } from "../models/app/ChatSubscription";
import { IMChat } from "../models/api/IMChat";
import { IMChatMetadata } from "../models/app/IMChatMetadata";
import { IMChatMsg } from "../models/app/IMChatMsg";
import { IMChatType } from "../models/api/IMChatType";
import { CHAT_DATE_FORMAT, CHAT_TIME_FORMAT, ONLINE } from "../models/constants";
import { ProgStatus } from "../models/utilTypes";
import ApiService from "../services/ApiService";
import { db } from "../services/DexieDbImpl";
import IMChatService from "../services/IMChatService";
import { IMChatSubscriptionService } from "../services/IMChatSubscriptionService";
import LocalStorageService from "../services/LocalStorageService";
import Logger from "../services/Logger";

import { rootStore } from "../StApp";
import PubKeyManagerService from "../services/PubKeyManagerService";


export class IMChatStore {


    chattingWithUid?: string | null
    chattingWithUserId!: string | null
    chatName: string
    chatMsgList: IMChatMsg[] = []

    chatText: string = ''
    chatAudio: string | null = null
    chatImg: string | null = null
    convStarted: boolean
    convStartTime: string | null = null

    chatterOnline = false

    prog: ProgStatus = 'SUCCESS'

    constructor(imChatMeta: IMChatMetadata) {

        this.chattingWithUid = imChatMeta.uid
        this.chattingWithUserId = imChatMeta.userName
        this.chatName = 'ANONYM'
        this.convStarted = imChatMeta.convStarted
        this.convStartTime = imChatMeta.convStartTime ?? null

        makeAutoObservable(this)
    }

    async loadPubKey() {
        if (!this.chattingWithUid)
            throw Error('Uid is empty')

        PubKeyManagerService.loadPubKey(this.chattingWithUid)

    }

    updateChatText(text: string) {
        this.chatText = text
    }

    loadChats() {

        db.getMsgsForChatter(this.chattingWithUid!)
            .then(resp => {
                let result = resp.map((item): IMChatMsg => ({
                    ...item,
                    time: moment(item.timestamp).format(CHAT_TIME_FORMAT),
                    dateOfSending: moment(item.timestamp).format(CHAT_DATE_FORMAT)
                }))

                this.chatMsgList = [...this.chatMsgList, ...result]
            })



    }

    blockUser(reported = false) {

        rootStore.mainStore.updateBlockList(this.chattingWithUid!)
        this.sendChat(IMChatType.BLOCK_USER)
        return ApiService.blockUser({
            reported: reported,
            reportedUid: this.chattingWithUid!
        })

    }

    deleteChat(){
        db.deleteChat(this.chattingWithUid!)
        db.deleteChatMsgsForChatter(this.chattingWithUid!)
    }

    subscribeToChat() {

        const subscription: ChatSubscription = {
            chatMsgHandler: (chat: IMChat) => { this.chatMsgRecepHandler(chat) },
            statusHandler: (status: string) => { this.chatterStatusHandler(status) }
        }

        if (this.chattingWithUid)
            IMChatSubscriptionService.Instance.subscribeToChat(this.chattingWithUid, subscription)
    }

    unsubscribeFromChat() {
        if (this.chattingWithUid)
            IMChatSubscriptionService.Instance.unsubscribe(this.chattingWithUid)
    }

    private chatMsgRecepHandler(chatMsg: IMChat) {

        if(chatMsg.imChatType == IMChatType.BLOCK_USER && chatMsg.senderUid == this.chattingWithUid ){
            this.deleteChat()
            PubKeyManagerService.removePubKey(this.chattingWithUid)
            return
        }
         
        const transChat: IMChatMsg = {
            ...chatMsg,
            dateOfSending: moment(chatMsg.timestamp).format(CHAT_DATE_FORMAT),
            time: moment(chatMsg.timestamp).format(CHAT_TIME_FORMAT)
        }

        this.chatMsgList = [...this.chatMsgList, transChat]
    }

    private chatterStatusHandler(status: string) {
        Logger.debug(status, "MQTT STATUS")
        if (status == ONLINE)
            this.chatterOnline = true
        else
            this.chatterOnline = false
    }

    startHello() {

        if (!this.chattingWithUid)
            return

        let dateTime = new Date().toISOString()

        ApiService.startHelloConv({
            uid: rootStore.mainStore.uid!,
            matchUid: this.chattingWithUid!,
            time: dateTime
        }).then(resp => {
            this.convStarted = true
            this.convStartTime = dateTime

            //Insert to Db

            //const chatMsg = this.insertChatToDb()
            this.insertChatHeadToDb()


        })

    }

    sendChat(imChatType: IMChatType = IMChatType.CHAT_TEXT ){
        if (!this.chattingWithUid)
            return

        const chatObj: IMChat = {
            data: this.chatText!,
            imChatType: imChatType,
            //recepId: this.chattingWithUserId!,
            recepUid: this.chattingWithUid,
            senderUid: rootStore.mainStore.uid!,
            senderId: rootStore.mainStore.nameOfUser!,
            timestamp: new Date().toISOString(),
            //user1: rootStore.mainStore.userName,
            //user2: this.chattingWithUserId!,
            convStartTime: this.convStartTime!
        }

        //Insert into db
        db.putChatMsg(chatObj)
        db.createChat({
            chatterId: this.chattingWithUserId!,
            chatterUid: this.chattingWithUid!,
            lastMsg: chatObj,
            lastMsgTime: chatObj.timestamp,
            convStartTime: this.convStartTime!,
            unread: false
        })

        this.chatMsgRecepHandler(chatObj)

        if (this.chatterOnline)
            IMChatService.sendOnlineChat(chatObj)
        else
            IMChatService.sendOfflineChat(chatObj)
    }

    private insertChatHeadToDb(chatObj?: IMChat) {

        db.createChat({
            chatterId: this.chattingWithUserId!,
            chatterUid: this.chattingWithUid!,
            lastMsg: chatObj,
            lastMsgTime: chatObj?.timestamp ?? this.convStartTime!,
            convStartTime: this.convStartTime!,
            unread: false
        })

    }

    private insertChatToDb() {

        const chatObj: IMChat = {
            data: this.chatText!,
            imChatType: IMChatType.CHAT_TEXT,
            //recepId: this.chattingWithUserId!,
            recepUid: this.chattingWithUid!,
            senderUid: rootStore.mainStore.uid!,
            senderId: rootStore.mainStore.userName,
            timestamp: new Date().toISOString(),
            //user1: rootStore.mainStore.userName,
            //user2: this.chattingWithUserId!,
            convStartTime: this.convStartTime!
        }

        //Insert into db
        db.putChatMsg(chatObj)

        return chatObj

    }
}